import { QueryErrorBoundary } from '@/catalog-apps/components/QueryErrorBoundary'
import { LoadingComponent } from '@/components/ui/LoadingComponent'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/shadcn/button'
import { EmptyState } from '@/components/ui/EmptyState'
import { Suspense, useEffect } from 'react'
import { Link, useLocation } from 'react-router'
import { DocsContent } from './DocsContent'
import { DocsSearch } from './DocsSearch'
import { DocsTableOfContents } from './DocsTableOfContents'
import { DOCS_PAGE_HEADER_TITLE } from './docsConstants'
import { docsPath } from './docsPaths'
import { useDocsPage } from './useDocsPage'

function DocsPageBody({ slug }: { slug: string }) {
  const location = useLocation()
  const { data: page, isLoading, isError } = useDocsPage(slug)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [slug])

  useEffect(() => {
    if (!location.hash || !page) {
      return
    }
    const id = location.hash.slice(1)
    const element = document.getElementById(id)
    element?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [location.hash, page])

  if (isLoading) {
    return <LoadingComponent text="Loading page..." />
  }

  if (isError || !page) {
    return (
      <EmptyState
        title="Page not found"
        description="This documentation page does not exist or has not been built yet."
        actions={
          <Button asChild variant="secondary">
            <Link to="/docs">Back to documentation home</Link>
          </Button>
        }
      />
    )
  }

  return (
    <div className="docs-page flex w-full justify-center gap-10 pb-10">
      <div className="docs-page-main">
        <DocsContent html={page.html} />
        {(page.prev || page.next) && (
          <nav className="mt-10 flex items-center justify-between gap-4 border-t pt-6 text-sm">
            {page.prev ? (
              <Link className="text-primary hover:underline" to={docsPath(page.prev)}>
                ← Previous
              </Link>
            ) : (
              <span />
            )}
            {page.next ? (
              <Link className="text-primary hover:underline" to={docsPath(page.next)}>
                Next →
              </Link>
            ) : (
              <span />
            )}
          </nav>
        )}
      </div>
      <DocsTableOfContents toc={page.toc} />
    </div>
  )
}

export function DocsPageView({ slug }: { slug: string }) {
  return (
    <>
      <PageHeader className="pr-3" title={DOCS_PAGE_HEADER_TITLE}>
        <DocsSearch />
      </PageHeader>
      <QueryErrorBoundary errorMessage="Failed to load documentation page">
        <Suspense fallback={<LoadingComponent text="Loading page..." />}>
          <DocsPageBody slug={slug} />
        </Suspense>
      </QueryErrorBoundary>
    </>
  )
}
